import { useMemo, useRef, useState } from 'react';
import type { Point } from '../lib/readingColumns';
import { severityOf, type ResolvedThreshold } from '../lib/thresholds';

const W = 720;
const H = 260;
const PAD = { l: 52, r: 14, t: 14, b: 26 };

function niceTicks(min: number, max: number, count = 5): number[] {
  if (min === max) return [min];
  const step0 = (max - min) / count;
  const mag = Math.pow(10, Math.floor(Math.log10(step0)));
  const norm = step0 / mag;
  const step = (norm >= 5 ? 5 : norm >= 2 ? 2 : 1) * mag;
  const start = Math.ceil(min / step) * step;
  const out: number[] = [];
  for (let v = start; v <= max + 1e-9; v += step) out.push(Number(v.toFixed(6)));
  return out;
}
const fmtVal = (v: number) => (Math.abs(v) >= 1000 ? Math.round(v).toLocaleString() : Math.round(v * 100) / 100);

export default function MetricChart({
  points, unit, threshold,
}: { points: Point[]; unit: string; threshold?: ResolvedThreshold | null }) {
  const ref = useRef<SVGSVGElement>(null);
  const [hover, setHover] = useState<number | null>(null);

  const model = useMemo(() => {
    if (!points.length) return null;
    const ts = points.map((p) => p.t);
    const vs = points.map((p) => p.v);
    const tMin = Math.min(...ts), tMax = Math.max(...ts);
    let lo = Math.min(...vs), hi = Math.max(...vs);
    // widen the value range so threshold bands are visible
    const bandVals = threshold
      ? [threshold.normalMin, threshold.normalMax, threshold.warnMin, threshold.warnMax].filter((x): x is number => x != null)
      : [];
    bandVals.forEach((x) => { lo = Math.min(lo, x); hi = Math.max(hi, x); });
    if (lo === hi) { lo -= 1; hi += 1; }
    const pad = (hi - lo) * 0.1;
    lo -= pad; hi += pad;
    const x = (t: number) => PAD.l + (tMax === tMin ? 0.5 : (t - tMin) / (tMax - tMin)) * (W - PAD.l - PAD.r);
    const y = (v: number) => H - PAD.b - ((v - lo) / (hi - lo)) * (H - PAD.t - PAD.b);
    return { tMin, tMax, lo, hi, x, y };
  }, [points, threshold]);

  if (!model) return <div className="chartEmpty">No readings for this metric in the selected range.</div>;
  const { x, y, lo, hi, tMin, tMax } = model;

  const clampY = (v: number) => Math.max(PAD.t, Math.min(H - PAD.b, y(v)));
  function band(min: number | null, max: number | null, cls: string) {
    const top = clampY(max ?? hi);
    const bot = clampY(min ?? lo);
    if (bot - top < 0.5) return null;
    return <rect className={cls} x={PAD.l} width={W - PAD.l - PAD.r} y={top} height={bot - top} />;
  }

  const yTicks = niceTicks(lo, hi);
  const spanDays = (tMax - tMin) / 86400000;
  const xCount = Math.min(5, points.length);
  const xTicks = Array.from({ length: xCount }, (_, i) =>
    tMin + (xCount <= 1 ? 0 : (i / (xCount - 1)) * (tMax - tMin)));
  const fmtX = (t: number) => new Date(t).toLocaleString([], spanDays > 2
    ? { day: '2-digit', month: 'short' } : { hour: '2-digit', minute: '2-digit' });

  const line = points.map((p, i) => `${i ? 'L' : 'M'}${x(p.t).toFixed(1)},${y(p.v).toFixed(1)}`).join(' ');
  const hp = hover != null ? points[hover] : null;

  function onMove(e: React.MouseEvent<SVGSVGElement>) {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * W;
    let best = 0, bd = Infinity;
    points.forEach((p, i) => { const d = Math.abs(x(p.t) - px); if (d < bd) { bd = d; best = i; } });
    setHover(best);
  }

  return (
    <svg ref={ref} viewBox={`0 0 ${W} ${H}`} className="metricChart" role="img"
      onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
      {threshold && (
        <g>
          {band(threshold.normalMin, threshold.normalMax, 'zone-ok')}
          {band(threshold.warnMin, threshold.normalMin ?? threshold.warnMin, 'zone-warn')}
          {band(threshold.normalMax ?? threshold.warnMax, threshold.warnMax, 'zone-warn')}
        </g>
      )}

      {yTicks.map((v) => (
        <g key={v}>
          <line className="grid" x1={PAD.l} x2={W - PAD.r} y1={y(v)} y2={y(v)} />
          <text className="axis" x={PAD.l - 7} y={y(v) + 3} textAnchor="end">{fmtVal(v)}</text>
        </g>
      ))}
      {xTicks.map((t, i) => (
        <text key={i} className="axis" x={x(t)} y={H - 8} textAnchor="middle">{fmtX(t)}</text>
      ))}

      <path className="metricLine" d={line} fill="none" />
      {points.map((p, i) => {
        const sv = severityOf(p.v, threshold ?? null);
        const cls = sv === 'critical' ? 'pt-bad' : sv === 'warning' ? 'pt-warn' : 'pt';
        return <circle key={i} className={cls} cx={x(p.t)} cy={y(p.v)} r={hover === i ? 4.5 : sv === 'normal' ? 2.5 : 3.5} />;
      })}

      {hp && (
        <g>
          <line className="crosshair" x1={x(hp.t)} x2={x(hp.t)} y1={PAD.t} y2={H - PAD.b} />
          <g transform={`translate(${Math.min(x(hp.t) + 8, W - 128)}, ${PAD.t + 2})`}>
            <rect className="tip" width={122} height={36} rx={7} />
            <text className="tip-t" x={9} y={15}>{new Date(hp.t).toLocaleString([], { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</text>
            <text className="tip-v" x={9} y={29}>{fmtVal(hp.v)}{unit ? ` ${unit}` : ''}</text>
          </g>
        </g>
      )}
    </svg>
  );
}
